import type * as THREE from 'three';
import type { Command } from '@/core/Command';
import { CompositeCommand } from '@/core/CompositeCommand';
import type { Vec3 } from '@/flora/FloraInstance';
import type { FaunaInstance } from '@/fauna/FaunaInstance';
import type { FaunaWorld } from '@/fauna/FaunaWorld';
import type { SiteObject } from '@/site/SiteObject';
import type { SiteObjectWorld } from '@/site/SiteObjectWorld';
import { RemoveFaunaCommand, UpdateFaunaCommand } from './FaunaCommands';
import { RemoveSiteObjectsCommand, UpdateSiteObjectCommand } from './SiteObjectCommands';

/**
 * What selecting, moving and deleting need to know about a placed thing that is not a plant.
 *
 * `StaffageTool` unified the *placement* gesture for fauna and objects on the grounds that arming,
 * ghosting, raycasting and clicking are the same act whatever is being dropped. This is the other
 * half of the same observation, arrived at from the other end: a bench and a rabbit are picked,
 * dragged, turned and deleted identically, and the two records already agree on every field those
 * four verbs touch — `id`, `position`, `rotationY`, `locked`, `hidden`.
 *
 * ## Why this is not `FloraInstance` as well
 *
 * A plant differs in exactly the two places that matter here, and both are load-bearing:
 *
 * - **`y` means the opposite thing.** A plant's `position.y` *is* its ground height, recomputed
 *   whenever the terrain moves (`FloraEditor.regroundPlants`). A creature's and an object's `y` is an
 *   *offset above* a ground height their worlds sample for themselves. So a plant drag writes
 *   `sampleHeight` into `y` and a staffage drag must leave `y` strictly alone — writing the ground
 *   height into a perched robin would drop it out of its tree, twice over.
 * - **There is a third gesture.** Maturity is a plant's whole life story and means nothing to a
 *   bench. See `FloraInstance` for why a creature has no maturity field to adjust.
 *
 * Everything else is shared, which is why the transform tool below drives both through one interface.
 */

/** The part of a placed record that moving, turning and deleting actually touch. */
export interface StaffageRecord {
  readonly id: string;
  readonly position: Vec3;
  readonly rotationY: number;
  readonly locked: boolean;
  readonly hidden: boolean;
}

/** A before/after pair for one record, the same shape `FloraCommands` records a transform in. */
export interface StaffageChange<T extends StaffageRecord> {
  before: T;
  after: T;
}

/**
 * One class of placeable thing, as selection and transform see it.
 *
 * Implementations are thin adapters over a world that already exists; none of them own state. The
 * two that exist are built by {@link createFaunaLayer} and {@link createPropLayer} at the bottom of
 * this file.
 */
export interface StaffageLayer<T extends StaffageRecord = StaffageRecord> {
  /** Matches the history `CommandKind` this layer's edits are filed under. */
  readonly kind: 'fauna' | 'object';
  /**
   * The `userData` key a built view carries back to its record, walked up from a raycast hit. Named
   * here rather than hard-coded in the picker so a third layer costs one object literal.
   */
  readonly viewIdKey: string;
  /** What one of these is called in a history entry when there are several: "creatures", "objects". */
  readonly plural: string;

  has(id: string): boolean;
  get(id: string): T | undefined;
  /** Every record this layer owns — what select-all and box-select enumerate. */
  all(): readonly T[];
  view(id: string): THREE.Object3D | undefined;
  /** Adopt an edited copy, rebuilding or reseating the view as the world sees fit. */
  update(item: T): void;

  /** A copy at a new ground position. `y` is deliberately untouched — see the note above. */
  movedTo(item: T, x: number, z: number): T;
  /** A copy turned by an increment about +Y. */
  turnedBy(item: T, radians: number): T;
  /** True when this record has changed in a way worth recording. */
  changed(before: T, after: T): boolean;

  /** Everything of the same species or model — what double-clicking one of them selects. */
  siblings(item: T): T[];
  /** What to call one of these in a history entry: "eastern cottontail", "bench". */
  describe(item: T): string;

  removeCommand(items: readonly T[]): Command;
  updateCommand(changes: readonly StaffageChange<T>[], label: string): Command;
}

/** Records that may be picked up: not locked, and actually on screen. */
export function isMovable(item: StaffageRecord): boolean {
  return !item.locked && !item.hidden;
}

/**
 * Fold several single-record updates into one history entry.
 *
 * The singular commands are what already existed and what is already tested, and a drag of one
 * creature — the overwhelmingly common case — still produces exactly that command rather than a
 * composite wrapping one part. Only a multi-selection pays for the wrapper.
 */
function foldUpdates(parts: readonly Command[], label: string): Command {
  const first = parts[0];
  if (parts.length === 1 && first) return first;
  return new CompositeCommand(parts, label);
}

function samePosition(a: Vec3, b: Vec3): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z;
}

/* --------------------------------------- Fauna --------------------------------------- */

/**
 * Placed creatures and figures.
 *
 * `describe` reads the name off the world's own catalog rather than taking one, because
 * `FaunaWorld.definitionFor` already tolerates a record this build no longer carries — and a history
 * entry is exactly the wrong place to start throwing over a dropped species.
 */
export function createFaunaLayer(world: FaunaWorld): StaffageLayer<FaunaInstance> {
  return {
    kind: 'fauna',
    viewIdKey: 'faunaInstanceId',
    plural: 'creatures',
    has: (id) => world.has(id),
    get: (id) => world.get(id),
    all: () => world.all(),
    view: (id) => world.view(id),
    update: (item) => world.update(item),
    movedTo: (item, x, z) => ({ ...item, position: { ...item.position, x, z } }),
    turnedBy: (item, radians) => ({ ...item, rotationY: item.rotationY + radians }),
    changed: (before, after) =>
      !samePosition(before.position, after.position) || before.rotationY !== after.rotationY,
    siblings: (item) =>
      world.all().filter((other) => other.faunaDefinitionId === item.faunaDefinitionId),
    describe: (item) => world.definitionFor(item)?.name.toLowerCase() ?? 'creature',
    removeCommand: (items) => {
      const only = items.length === 1 ? items[0] : undefined;
      return new RemoveFaunaCommand(
        world,
        items,
        only
          ? `Remove ${world.definitionFor(only)?.name.toLowerCase() ?? 'creature'}`
          : `Remove ${items.length} creatures`,
      );
    },
    updateCommand: (changes, label) =>
      foldUpdates(
        changes.map((change) => new UpdateFaunaCommand(world, change.before, change.after, label)),
        label,
      ),
  };
}

/* --------------------------------------- Objects --------------------------------------- */

/**
 * Placed props — the Objects palette's output, and only that.
 *
 * Buildings and paving live in the same world and are deliberately left out. They are not staffage:
 * they arrive from the reference pipeline and the wand as the site's *given* context, every plant on
 * the site was placed against where they are, and `blockingObject` refuses planting through them. A
 * house that can be swept up by a box-select and deleted with one key is a different feature with a
 * different set of consequences to think through — see `TODO` 15.25.
 */
export function createPropLayer(world: SiteObjectWorld): StaffageLayer<SiteObject> {
  const isProp = (object: SiteObject): boolean => object.kind === 'prop';
  const propOrUndefined = (id: string): SiteObject | undefined => {
    const object = world.get(id);
    return object && isProp(object) ? object : undefined;
  };

  return {
    kind: 'object',
    viewIdKey: 'siteObjectId',
    plural: 'objects',
    has: (id) => propOrUndefined(id) !== undefined,
    get: propOrUndefined,
    all: () => world.all().filter(isProp),
    view: (id) => (propOrUndefined(id) ? world.view(id) : undefined),
    update: (item) => world.update(item),
    movedTo: (item, x, z) => ({ ...item, position: { ...item.position, x, z } }),
    turnedBy: (item, radians) => ({ ...item, rotationY: item.rotationY + radians }),
    changed: (before, after) =>
      !samePosition(before.position, after.position) || before.rotationY !== after.rotationY,
    // By model rather than by name: two catalog entries can share a display name, and the model id is
    // what actually decides whether two benches are the same bench. Props with no asset yet fall back
    // to the name, which is the only thing left that distinguishes them.
    siblings: (item) =>
      world
        .all()
        .filter(isProp)
        .filter((other) =>
          item.modelId ? other.modelId === item.modelId : other.name === item.name,
        ),
    describe: (item) => item.name.toLowerCase(),
    removeCommand: (items) => {
      const only = items.length === 1 ? items[0] : undefined;
      return new RemoveSiteObjectsCommand(
        world,
        items,
        only ? `Remove ${only.name.toLowerCase()}` : `Remove ${items.length} objects`,
      );
    },
    updateCommand: (changes, label) =>
      foldUpdates(
        changes.map(
          (change) => new UpdateSiteObjectCommand(world, change.before, change.after, label),
        ),
        label,
      ),
  };
}
